import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import securityApi from "apis/eduidSecurity";
import { EduIDButton } from "components/Common/EduIDButton";
import { InputWrapper } from "components/Common/InputWrapper";
import { ShowAndHideButton } from "components/Common/ShowAndHideButton";
import { useAppSelector } from "eduid-hooks";
import { useState } from "react";
import { Field as FinalField, Form as FinalForm } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
function validateNin(value) {
    if (!value) {
        return "required";
    }
    // accept only digits
    if (/\D+/.test(value))
        return "nins.illegal_chars";
    if (value.length !== 12)
        return "nins.wrong_length";
    value = value.slice(2); // To pass the Luhn check only use the 10 last digits
    // The Luhn Algorithm. It's so pretty.
    // taken from https://gist.github.com/DiegoSalazar/4075533/
    let nCheck = 0, bEven = false;
    for (let n = value.length - 1; n >= 0; n--) {
        const cDigit = value.charAt(n);
        let nDigit = Number.parseInt(cDigit, 10);
        if (bEven) {
            if ((nDigit *= 2) > 9)
                nDigit -= 9;
        }
        nCheck += nDigit;
        bEven = !bEven;
    }
    if (nCheck % 10 !== 0) {
        return "nins.invalid_nin";
    }
    return undefined;
}
function NinInput({ meta, input, placeholder, label, helpBlock }) {
    const [showNin, setShowNin] = useState(false);
    let className = "is-valid";
    if (meta.touched || meta.submitFailed) {
        if (meta.invalid) {
            className = "is-invalid";
        }
    }
    const maskValue = (v) => {
        return "\u2022".repeat(v.length);
    };
    const value = input.value || "";
    const displayValue = showNin ? value : maskValue(value);
    return (_jsx(InputWrapper, { label: label, helpBlock: helpBlock, meta: meta, input: input, children: _jsxs("div", { className: "password-input", children: [_jsx(ShowAndHideButton, { isShown: showNin, onClick: () => setShowNin(!showNin) }), _jsx("input", { type: "text", value: displayValue, onChange: (e) => {
                        const inputVal = e.target.value;
                        if (showNin) {
                            input.onChange(inputVal);
                        }
                        else {
                            if (inputVal.length < displayValue.length) {
                                input.onChange(value.slice(0, -1));
                                return;
                            }
                            const lastChar = inputVal.slice(-1);
                            if (inputVal.length > displayValue.length) {
                                input.onChange(value + lastChar);
                            }
                        }
                    }, onBlur: input.onBlur, id: input.name, className: className, placeholder: placeholder })] }) }));
}
export function NinForm() {
    const nin = useAppSelector((state) => { var _a, _b, _c; return (_c = (_b = (_a = state.personal_data) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.identities) === null || _c === void 0 ? void 0 : _c.nin; });
    const [addNin] = securityApi.useLazyAddNinQuery();
    const intl = useIntl();
    // placeholder can't be an Element, we need to get the actual translated string here
    const placeholder = intl.formatMessage({
        id: "ninForm.swedish",
        defaultMessage: "yyyymmddnnnn",
        description: "Swedish NIN template",
    });
    function submitNinForm(values) {
        const nin = values.nin;
        if (nin) {
            addNin({ nin });
        }
    }
    return (_jsx(FinalForm, { onSubmit: submitNinForm, initialValues: {
            nin: (nin === null || nin === void 0 ? void 0 : nin.number) || "",
        }, render: ({ handleSubmit, pristine, invalid }) => {
            return (_jsxs("form", { onSubmit: handleSubmit, className: "single-input-form x-adjust", children: [_jsx(FinalField, { component: NinInput, componentClass: "input", type: "text", name: "nin", label: _jsx(FormattedMessage, { id: "ninForm.label", description: "nin label", defaultMessage: "ID number" }), placeholder: placeholder, helpBlock: _jsx(FormattedMessage, { id: "ninForm.nins", description: "nins input help text", defaultMessage: "12 digits" }), validate: validateNin }), _jsx("div", { className: "buttons", children: _jsx(EduIDButton, { id: "add-nin-button", buttonstyle: "primary", disabled: pristine || invalid, type: "submit", children: _jsx(FormattedMessage, { id: "common.add", description: "button_add", defaultMessage: "Add" }) }) })] }));
        } }));
}
